import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Linkedin, MapPin, Calendar, TrendingUp,
  Play, Users, FileText, ExternalLink, X,
  Briefcase, Target, BarChart3, Clock, ChevronRight,
  IndianRupee, Zap, MapPinned, Brain,
  Shield, Sparkles, ArrowRight, Eye, Repeat, TrendingDown,
} from "lucide-react";

import { RichTextDark } from "@/components/ui/rich-text";

const BASE = import.meta.env.BASE_URL;

interface Company {
  name: string;
  description: string | null;
  sector: string | null;
  stage: string | null;
  website: string | null;
  logoUrl: string | null;
  foundedYear: number | null;
  location: string | null;
  thesis: string | null;
  businessModel: string | null;
}

interface Founder {
  id: number;
  name: string;
  role: string | null;
  bio: string | null;
  avatarUrl: string | null;
  linkedinUrl: string | null;
}

interface KPI {
  id: number;
  metric: string;
  value: string;
  unit: string | null;
  recordedAt: string;
}

interface Update {
  id: number;
  weekOf: string;
  highlights: string | null;
  challenges: string | null;
  asks: string | null;
}

interface Document {
  id: number;
  title: string;
  type: string;
  url: string | null;
  externalUrl: string | null;
}

interface MediaAsset {
  id: number;
  title: string;
  type: string;
  url: string;
  thumbnailUrl: string | null;
  description: string | null;
}

interface MeeraIntelData {
  summary?: string;
  traction?: {
    revenue?: string;
    users?: string;
    growth?: string;
    keyMetrics?: string[];
  };
  funding?: {
    totalRaised?: string;
    currentRound?: string;
    valuation?: string;
  };
  fundraising?: {
    ask?: string;
    desiredValuation?: string;
    fundraisingHistory?: string;
    promotersCapital?: string;
    fundUtilization?: Array<{ category: string; percentage: string }>;
  };
  financials?: {
    revenueActual?: string;
    revenueProjected?: string;
    monthlyBurn?: string;
    cashRunway?: string;
    ebitda?: string;
    grossMargin?: string;
    cashInBank?: string;
  };
  geography?: string;
  problemStatement?: string;
  competitiveAdvantage?: string;
  competitiveLandscape?: Array<{ name: string; description?: string }>;
  capTable?: Array<{ name: string; role?: string; equity: string }>;
  productPortfolio?: Array<{ name: string; description?: string; pricing?: string }>;
  distributionModel?: string;
  customerProfile?: string;
  advisors?: Array<{ name: string; role?: string; description?: string; linkedinUrl?: string }>;
  teamSize?: number;
  confidence?: number;
}

const stageColors: Record<string, string> = {
  pipeline: "bg-slate-600",
  onboarding: "bg-blue-600",
  active: "bg-emerald-600",
  graduating: "bg-amber-600",
  exited: "bg-purple-600",
};

function formatKPI(value: string, unit: string | null) {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  if (unit === "INR" || unit === "USD") {
    const symbol = unit === "INR" ? "₹" : "$";
    if (num >= 10000000) return `${symbol}${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `${symbol}${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${symbol}${(num / 1000).toFixed(1)}K`;
    return `${symbol}${num.toLocaleString()}`;
  }
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

export default function PublicShowcase({ slug }: { slug: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [founders, setFounders] = useState<Founder[]>([]);
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [updates, setUpdates] = useState<Update[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [meeraIntel, setMeeraIntel] = useState<MeeraIntelData | null>(null);
  const [activeClip, setActiveClip] = useState<MediaAsset | null>(null);

  const getEmbedUrl = (url: string): string | null => {
    if (url.includes("shubhlabh") || url.includes("/clips/") || url.includes("/c/") || url.includes("/watch/")) {
      return url.replace(/\/(c|clips)\//, "/watch/");
    }
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    return null;
  };

  const isEmbeddableMedia = (m: MediaAsset): boolean => {
    if (!m.url) return false;
    if (m.type === "clip" || m.type === "video") return true;
    return !!getEmbedUrl(m.url);
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}api/showcase/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setCompany(data.company);
        setFounders(data.founders);
        setKpis(data.kpis);
        setUpdates(data.updates);
        setDocuments(data.documents);
        setMedia(data.media);
        if (data.meeraIntel) setMeeraIntel(data.meeraIntel);
      } else {
        setError("This showcase is not available.");
      }
    } catch {
      setError("Unable to load showcase.");
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#FF5C00]/20" />
          <div className="h-4 w-32 bg-white/10 rounded" />
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 text-lg">{error || "Showcase not found"}</p>
          <p className="text-white/30 text-sm mt-2">This showcase may have been unpublished or doesn't exist.</p>
        </div>
      </div>
    );
  }

  const latestKpis = kpis.reduce((acc, kpi) => {
    if (!acc[kpi.metric] || new Date(kpi.recordedAt) > new Date(acc[kpi.metric].recordedAt)) {
      acc[kpi.metric] = kpi;
    }
    return acc;
  }, {} as Record<string, KPI>);
  const kpiList = Object.values(latestKpis).slice(0, 6);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: "radial-gradient(circle at 30% 40%, #FF5C00 0%, transparent 50%), radial-gradient(circle at 80% 60%, #FF5C00 0%, transparent 40%)"
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          <div className="flex items-start gap-6 mb-8">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt="" className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-3xl font-bold font-serif">
                {company.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold font-serif tracking-tight mb-3">{company.name}</h1>
              <div className="flex flex-wrap items-center gap-3 text-white/60 text-sm mb-4">
                {company.sector && (
                  <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{company.sector}</span>
                )}
                {company.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{company.location}</span>
                )}
                {company.foundedYear && (
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{company.foundedYear}</span>
                )}
                {company.stage && (
                  <Badge className={`${stageColors[company.stage] || "bg-slate-600"} text-white text-xs`}>
                    {company.stage}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {company.description && (
            <RichTextDark text={company.description} className="text-white/70 text-lg leading-relaxed max-w-3xl mb-6" />
          )}

          <div className="flex flex-wrap gap-6 text-sm">
            {company.thesis && (
              <div className="flex-1 min-w-[200px] bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-[#FF5C00] mb-2 font-medium">
                  <Briefcase className="w-4 h-4" /> Investment Thesis
                </div>
                <RichTextDark text={company.thesis} className="text-white/60" />
              </div>
            )}
            {company.businessModel && (
              <div className="flex-1 min-w-[200px] bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-[#FF5C00] mb-2 font-medium">
                  <BarChart3 className="w-4 h-4" /> Business Model
                </div>
                <RichTextDark text={company.businessModel} className="text-white/60" />
              </div>
            )}
          </div>

          {company.website && (
            <a href={company.website} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-[#FF5C00] hover:underline text-sm">
              <Globe className="w-4 h-4" /> {company.website}
            </a>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 space-y-16 pb-20">
        {meeraIntel && (meeraIntel.funding || meeraIntel.traction || meeraIntel.summary) && (
          <section>
            {meeraIntel.summary && (
              <div className="mb-8 bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <Brain className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-2">Investment Overview</p>
                    <RichTextDark text={meeraIntel.summary} className="text-white/70 text-base leading-relaxed" />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {meeraIntel.funding && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-emerald-400 mb-3">
                    <IndianRupee className="w-4 h-4" />
                    <p className="text-xs uppercase tracking-wider font-medium">Funding</p>
                  </div>
                  {meeraIntel.funding.totalRaised && (
                    <p className="text-2xl font-bold text-[#FF5C00] font-mono mb-1">{meeraIntel.funding.totalRaised}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {meeraIntel.funding.currentRound && (
                      <Badge className="bg-white/10 text-white/80 text-xs">{meeraIntel.funding.currentRound}</Badge>
                    )}
                    {meeraIntel.funding.valuation && (
                      <span className="text-white/40 text-xs">@ {meeraIntel.funding.valuation}</span>
                    )}
                  </div>
                </div>
              )}

              {meeraIntel.traction && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-emerald-400 mb-3">
                    <Zap className="w-4 h-4" />
                    <p className="text-xs uppercase tracking-wider font-medium">Traction</p>
                  </div>
                  <div className="space-y-1.5">
                    {meeraIntel.traction.revenue && (
                      <p className="text-sm text-white/80"><span className="text-white font-medium">Revenue:</span> {meeraIntel.traction.revenue}</p>
                    )}
                    {meeraIntel.traction.users && (
                      <p className="text-sm text-white/80"><span className="text-white font-medium">Users:</span> {meeraIntel.traction.users}</p>
                    )}
                    {meeraIntel.traction.growth && (
                      <p className="text-sm text-white/80"><span className="text-white font-medium">Growth:</span> {meeraIntel.traction.growth}</p>
                    )}
                  </div>
                </div>
              )}

              {meeraIntel.geography && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-emerald-400 mb-3">
                    <MapPinned className="w-4 h-4" />
                    <p className="text-xs uppercase tracking-wider font-medium">Markets</p>
                  </div>
                  <p className="text-sm text-white/70">{meeraIntel.geography}</p>
                </div>
              )}
            </div>

            {meeraIntel.traction?.keyMetrics && meeraIntel.traction.keyMetrics.length > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {meeraIntel.traction.keyMetrics.map((metric, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    <p className="text-xs text-white/60">{metric}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {meeraIntel?.problemStatement && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#FF5C00]" /> Problem & Opportunity
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <RichTextDark text={meeraIntel.problemStatement} className="text-white/70 text-sm leading-relaxed" />
            </div>
          </section>
        )}

        {meeraIntel?.competitiveAdvantage && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#FF5C00]" /> Competitive Advantage
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <RichTextDark text={meeraIntel.competitiveAdvantage} className="text-white/70 text-sm leading-relaxed" />
            </div>
          </section>
        )}

        {meeraIntel?.productPortfolio && meeraIntel.productPortfolio.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#FF5C00]" /> Product Portfolio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {meeraIntel.productPortfolio.map((p, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-sm font-medium text-white mb-1">{p.name}</p>
                  {p.description && <p className="text-white/50 text-xs mb-2">{p.description}</p>}
                  {p.pricing && (
                    <span className="text-[#FF5C00] text-xs font-mono">{p.pricing}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {(meeraIntel?.financials || meeraIntel?.fundraising) && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FF5C00]" /> Financial Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meeraIntel.financials && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-3">Key Financials</p>
                  <div className="space-y-2">
                    {meeraIntel.financials.revenueActual && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Revenue (Actual)</span>
                        <span className="text-white font-medium">{meeraIntel.financials.revenueActual}</span>
                      </div>
                    )}
                    {meeraIntel.financials.revenueProjected && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Revenue (Projected)</span>
                        <span className="text-white font-medium">{meeraIntel.financials.revenueProjected}</span>
                      </div>
                    )}
                    {meeraIntel.financials.grossMargin && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Gross Margin</span>
                        <span className="text-emerald-400 font-medium">{meeraIntel.financials.grossMargin}</span>
                      </div>
                    )}
                    {meeraIntel.financials.ebitda && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">EBITDA</span>
                        <span className="text-white font-medium">{meeraIntel.financials.ebitda}</span>
                      </div>
                    )}
                    {meeraIntel.financials.monthlyBurn && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Monthly Burn</span>
                        <span className="text-white font-medium">{meeraIntel.financials.monthlyBurn}</span>
                      </div>
                    )}
                    {meeraIntel.financials.cashRunway && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Cash Runway</span>
                        <span className="text-white font-medium">{meeraIntel.financials.cashRunway}</span>
                      </div>
                    )}
                    {meeraIntel.financials.cashInBank && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Cash in Bank</span>
                        <span className="text-white font-medium">{meeraIntel.financials.cashInBank}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {meeraIntel.fundraising && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-3">Fundraise</p>
                  <div className="space-y-2">
                    {meeraIntel.fundraising.ask && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Raising</span>
                        <span className="text-[#FF5C00] font-bold text-lg">{meeraIntel.fundraising.ask}</span>
                      </div>
                    )}
                    {meeraIntel.fundraising.desiredValuation && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Target Valuation</span>
                        <span className="text-white font-medium">{meeraIntel.fundraising.desiredValuation}</span>
                      </div>
                    )}
                    {meeraIntel.fundraising.promotersCapital && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Promoters' Capital</span>
                        <span className="text-white font-medium">{meeraIntel.fundraising.promotersCapital}</span>
                      </div>
                    )}
                    {meeraIntel.fundraising.fundraisingHistory && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Prior Rounds</span>
                        <span className="text-white font-medium">{meeraIntel.fundraising.fundraisingHistory}</span>
                      </div>
                    )}
                  </div>
                  {meeraIntel.fundraising.fundUtilization && meeraIntel.fundraising.fundUtilization.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Use of Funds</p>
                      <div className="space-y-1.5">
                        {meeraIntel.fundraising.fundUtilization.map((fu, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-white/60">{fu.category}</span>
                            <span className="text-white/80 font-mono">{fu.percentage}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {meeraIntel?.capTable && meeraIntel.capTable.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#FF5C00]" /> Cap Table
            </h2>
            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <div className="grid grid-cols-3 gap-4 px-5 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                <span>Shareholder</span>
                <span>Role</span>
                <span className="text-right">Equity</span>
              </div>
              {meeraIntel.capTable.map((row, i) => (
                <div key={i} className="grid grid-cols-3 gap-4 px-5 py-3 border-b border-white/[0.05] last:border-0">
                  <span className="text-sm text-white">{row.name}</span>
                  <span className="text-sm text-white/50">{row.role || "—"}</span>
                  <span className="text-sm text-[#FF5C00] font-mono text-right">{row.equity}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {meeraIntel?.competitiveLandscape && meeraIntel.competitiveLandscape.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#FF5C00]" /> Competitive Landscape
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {meeraIntel.competitiveLandscape.map((c, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-sm font-medium text-white mb-1">{c.name}</p>
                  {c.description && <p className="text-white/50 text-xs">{c.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {(meeraIntel?.distributionModel || meeraIntel?.customerProfile) && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FF5C00]" /> Go-to-Market
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meeraIntel.distributionModel && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-2">Distribution</p>
                  <RichTextDark text={meeraIntel.distributionModel} className="text-white/70 text-sm leading-relaxed" />
                </div>
              )}
              {meeraIntel.customerProfile && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-2">Customer Profile</p>
                  <RichTextDark text={meeraIntel.customerProfile} className="text-white/70 text-sm leading-relaxed" />
                </div>
              )}
            </div>
          </section>
        )}

        {founders.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#FF5C00]" /> Founding Team
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {founders.map((f) => (
                <div key={f.id} className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-start gap-4">
                  {f.avatarUrl ? (
                    <img src={f.avatarUrl} alt="" className="w-14 h-14 rounded-full object-cover border border-white/20" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#FF5C00]/20 flex items-center justify-center text-lg font-bold text-[#FF5C00]">
                      {f.name.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{f.name}</h3>
                    </div>
                    {f.role && <p className="text-[#FF5C00] text-sm">{f.role}</p>}
                    {f.bio && <RichTextDark text={f.bio} className="text-white/50 text-sm mt-1" />}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {meeraIntel?.advisors && meeraIntel.advisors.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#FF5C00]" /> Advisors & Strategic Partners
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {meeraIntel.advisors.map((a, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-white">{a.name}</p>
                    {a.linkedinUrl && (
                      <a href={a.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                        <Linkedin className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  {a.role && <p className="text-[#FF5C00] text-xs mb-1">{a.role}</p>}
                  {a.description && <p className="text-white/50 text-xs">{a.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {kpiList.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#FF5C00]" /> Traction Snapshot
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {kpiList.map((kpi) => (
                <div key={kpi.id} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                  <p className="text-2xl md:text-3xl font-bold text-[#FF5C00] font-mono">
                    {formatKPI(kpi.value, kpi.unit)}
                  </p>
                  <p className="text-white/50 text-sm mt-1 capitalize">{kpi.metric.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {media.length > 0 && (
          <section>
            <div className="mb-6">
              <h2 className="text-2xl font-bold font-serif flex items-center gap-2">
                <Play className="w-5 h-5 text-[#FF5C00]" /> Media & Coverage
              </h2>
              <p className="text-white/40 text-sm mt-1 ml-7 italic">Kal Ke Krorepati — Shubh Labh Show</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {media.map((m) => {
                const embeddable = isEmbeddableMedia(m);
                return embeddable ? (
                  <button key={m.id} onClick={() => setActiveClip(m)}
                    className="group bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#FF5C00]/30 transition-colors text-left">
                    {m.thumbnailUrl ? (
                      <div className="relative aspect-video">
                        <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                          <div className="w-12 h-12 rounded-full bg-[#FF5C00] flex items-center justify-center shadow-lg">
                            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-white/5 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-[#FF5C00]/20 flex items-center justify-center group-hover:bg-[#FF5C00]/40 transition-colors">
                          <Play className="w-5 h-5 text-[#FF5C00]" fill="currentColor" />
                        </div>
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-medium truncate">{m.title}</p>
                      {m.description && <p className="text-white/40 text-xs mt-1 line-clamp-2">{m.description}</p>}
                    </div>
                  </button>
                ) : (
                  <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer"
                    className="group bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-[#FF5C00]/30 transition-colors">
                    {m.thumbnailUrl ? (
                      <div className="relative aspect-video">
                        <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ExternalLink className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-white/5 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-white/30" />
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-sm font-medium truncate">{m.title}</p>
                      {m.description && <p className="text-white/40 text-xs mt-1 line-clamp-2">{m.description}</p>}
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        )}

        {activeClip && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActiveClip(null)}>
            <div className="relative w-full max-w-4xl bg-[#111] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{activeClip.title}</p>
                  {activeClip.description && <p className="text-white/40 text-xs truncate">{activeClip.description}</p>}
                </div>
                <button onClick={() => setActiveClip(null)}
                  className="ml-3 p-1.5 rounded-full hover:bg-white/10 transition-colors flex-shrink-0">
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
              <div className="aspect-video">
                <iframe
                  src={getEmbedUrl(activeClip.url || "") || activeClip.url}
                  className="w-full h-full border-0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}

        {updates.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#FF5C00]" /> Recent Updates
            </h2>
            <div className="space-y-4">
              {updates.slice(0, 5).map((u) => (
                <div key={u.id} className="bg-white/5 border border-white/10 rounded-xl p-5">
                  <p className="text-[#FF5C00] text-sm font-medium mb-2">
                    Week of {new Date(u.weekOf).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                  {u.highlights && (
                    <div className="mb-2">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Highlights</p>
                      <p className="text-white/70 text-sm">{u.highlights}</p>
                    </div>
                  )}
                  {u.challenges && (
                    <div className="mb-2">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Challenges</p>
                      <p className="text-white/70 text-sm">{u.challenges}</p>
                    </div>
                  )}
                  {u.asks && (
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Asks</p>
                      <p className="text-white/70 text-sm">{u.asks}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {documents.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold font-serif mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF5C00]" /> Documents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {documents.map((d) => (
                <a key={d.id} href={d.externalUrl || d.url || "#"} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#FF5C00]/30 transition-colors">
                  <FileText className="w-5 h-5 text-[#FF5C00] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.title}</p>
                    <p className="text-white/30 text-xs">{d.type}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/30" />
                </a>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="border-t border-white/10 mt-16">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <p className="text-[#FF5C00] text-xs font-mono uppercase tracking-[0.3em] mb-3">Kal Ke Krorepati — Shubh Labh</p>
            <h2 className="text-3xl md:text-4xl font-bold font-serif tracking-tight mb-4">
              Favcy <span className="text-[#FF5C00]">Select</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto leading-relaxed">
              Own a piece of tomorrow's breakout companies — hand-picked, media-amplified, and venture-built by Favcy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#FF5C00]/20 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center mb-4">
                <Eye className="w-5 h-5 text-[#FF5C00]" />
              </div>
              <h3 className="text-lg font-bold font-serif mb-2">Why</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Favcy's accelerated companies are amplified through national media on <span className="text-white/70 font-medium">Kal Ke Krorepati</span>, giving them visibility, credibility, and momentum that most early-stage startups never access. As a Favcy Select investor, you ride this wave from day one.
              </p>
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#FF5C00]/20 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center mb-4">
                <Sparkles className="w-5 h-5 text-[#FF5C00]" />
              </div>
              <h3 className="text-lg font-bold font-serif mb-2">What</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Favcy Select lets you acquire a small stake across a curated portfolio of venture-built companies by purchasing secondary equity from Favcy VB. You gain exposure to multiple high-potential startups in a single move — diversified, de-risked, and backed by India's leading venture builder.
              </p>
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#FF5C00]/20 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center mb-4">
                <Repeat className="w-5 h-5 text-[#FF5C00]" />
              </div>
              <h3 className="text-lg font-bold font-serif mb-2">How</h3>
              <p className="text-white/50 text-sm leading-relaxed">
                Follow each company's journey through these showcase pages. When you're ready to go deeper, utilise Favcy's <span className="text-white/70 font-medium">Right of First Refusal (ROFR)</span> to invest directly in subsequent rounds — at terms you've already validated by watching the company grow.
              </p>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-14">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-sm font-bold font-serif">F</div>
                <div>
                  <p className="text-sm font-bold font-serif">Favcy Venture Builders</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">India's Leading Venture Builder</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div>
                <p className="text-white/30 text-xs uppercase tracking-wider mb-1">Portfolio Approach</p>
                <p className="text-white/60">Secondary equity across select portfolio companies — one investment, multiple bets</p>
              </div>
              <div>
                <p className="text-white/30 text-xs uppercase tracking-wider mb-1">Media Amplification</p>
                <p className="text-white/60">Companies featured on Kal Ke Krorepati — Shubh Labh national show</p>
              </div>
              <div>
                <p className="text-white/30 text-xs uppercase tracking-wider mb-1">Follow-on Rights</p>
                <p className="text-white/60">ROFR access to invest directly in subsequent funding rounds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] bg-[#060606]">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-xs font-bold font-serif">F</div>
              <span className="text-sm font-semibold font-serif">Favcy</span>
              <span className="text-white/20 text-xs">|</span>
              <span className="text-white/40 text-xs font-mono uppercase tracking-wider">Favcy Select</span>
            </div>
            <p className="text-white/30 text-xs">
              © {new Date().getFullYear()} Favcy Venture Builders Private Limited. All rights reserved.
            </p>
          </div>

          <div className="border-t border-white/[0.06] pt-6">
            <div className="flex items-start gap-2 mb-3">
              <Shield className="w-4 h-4 text-white/20 mt-0.5 flex-shrink-0" />
              <p className="text-[10px] text-white/20 uppercase tracking-wider font-medium">Important Disclaimer</p>
            </div>
            <div className="text-[11px] text-white/25 leading-relaxed space-y-2 pl-6">
              <p>
                This showcase page is provided for informational purposes only. The information presented herein has been prepared by Favcy Venture Builders based on data provided by the featured company.
              </p>
              <p>
                Favcy Select involves the purchase of secondary equity in early-stage companies. Investing in startups and early-stage companies involves a high degree of risk, including the potential loss of your entire investment. Past performance is not indicative of future results. The companies featured on "Kal Ke Krorepati — Shubh Labh" receive media exposure as part of Favcy's venture building program; such media coverage does not constitute an endorsement or guarantee of the company's future performance.
              </p>
              <p>
                The Right of First Refusal (ROFR) is subject to the terms and conditions of the Favcy Select agreement and may be modified or withdrawn at Favcy's discretion. By accessing this page, you acknowledge that you have read and understood this disclaimer.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
